import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, QrCode, X } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import api from "@/lib/api";
import { sound } from "@/lib/sound";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// Pull the invite code out of a scanned value — supports a full /j/{code}
// link (what the host's QR encodes) or a bare code.
function extractCode(text) {
  const raw = (text || "").trim();
  const m = raw.match(/\/j\/([A-Za-z0-9]+)/i);
  if (m) return m[1];
  try {
    const u = new URL(raw);
    const seg = u.pathname.split("/").filter(Boolean);
    if (seg.length) return seg[seg.length - 1];
  } catch {
    /* not a URL — treat as a raw code */
  }
  return raw;
}

export default function JoinBill() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);

  const stopScan = useCallback(async () => {
    const inst = scannerRef.current;
    scannerRef.current = null;
    if (inst) {
      try {
        await inst.stop();
        inst.clear();
      } catch {
        /* already stopped */
      }
    }
    setScanning(false);
  }, []);

  useEffect(() => () => { stopScan(); }, [stopScan]);

  const joinWithCode = useCallback(
    async (rawCode) => {
      const clean = (rawCode || "").trim().toUpperCase();
      if (clean.length < 4) return;
      setBusy(true);
      try {
        const r = await api.post("/bills/join", { invite_code: clean });
        const billId = r.data?.bill?.id || r.data?.id || r.data?.bill_id;
        sound.play("success");
        toast.success("Joined the bill");
        if (billId) navigate(`/bill/${billId}`);
        else navigate("/");
      } catch (err) {
        sound.play("error");
        toast.error(err.response?.data?.detail || "Invalid invite code");
        setBusy(false);
      }
    },
    [navigate]
  );

  const startScan = async () => {
    sound.play("tap");
    setScanning(true);
    // let the #qr-reader node mount before starting the camera
    setTimeout(async () => {
      try {
        const html5 = new Html5Qrcode("qr-reader");
        scannerRef.current = html5;
        await html5.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 230, height: 230 } },
          async (decodedText) => {
            await stopScan();
            const c = extractCode(decodedText);
            setCode(c.toUpperCase());
            joinWithCode(c);
          },
          () => {} // per-frame decode errors — ignore
        );
      } catch {
        toast.error("Couldn't open the camera — enter the code instead");
        await stopScan();
      }
    }, 60);
  };

  const submit = (e) => {
    e.preventDefault();
    joinWithCode(code);
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="hp-glass rounded-3xl p-8 hp-fade-in">
        <div className="h-12 w-12 rounded-2xl grid place-items-center bg-[#7c3aed]/25 text-[#c084fc] mb-4">
          <Users size={24} />
        </div>
        <h1 className="text-2xl font-extrabold text-white">Join a bill</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Scan the host&apos;s QR code, or enter the invite code they shared.
        </p>

        {/* Camera scanner */}
        {scanning ? (
          <div className="mt-6">
            <div
              id="qr-reader"
              data-testid="qr-scanner"
              className="w-full overflow-hidden rounded-2xl border border-[#a855f7]/40 bg-black [&_video]:rounded-2xl [&_video]:w-full"
            />
            <Button
              data-testid="cancel-scan-btn"
              onClick={stopScan}
              className="w-full mt-3 h-11 rounded-xl font-bold bg-white/10 text-white hover:bg-white/15 border-0 gap-2"
            >
              <X size={18} /> Cancel scan
            </Button>
          </div>
        ) : (
          <button
            data-testid="scan-qr-btn"
            onClick={startScan}
            disabled={busy}
            className="hp-cta w-full mt-6 h-12 rounded-xl font-bold text-white border-0 flex items-center justify-center gap-2"
          >
            <QrCode size={20} /> Scan QR code
          </button>
        )}

        {!scanning && (
          <>
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-xs text-muted-foreground">or enter code</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            <form onSubmit={submit} className="space-y-4">
              <Input
                data-testid="join-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="ABC123"
                maxLength={6}
                className="bg-white/5 border-white/10 text-white text-center text-2xl tracking-[0.4em] font-bold h-14"
              />
              <Button
                data-testid="join-submit"
                type="submit"
                disabled={busy || code.trim().length < 4}
                className="w-full h-11 rounded-xl font-bold bg-white/10 text-white hover:bg-white/15 border-0"
              >
                {busy ? "Joining…" : "Join bill"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
